/**
 * Module-level bridge for "open an in-pane chat sheet" requests.
 *
 * The mirror image of `integrations/tools/deckorate/nav-bridge.ts`: that one
 * carries chat → Deckorate ("open this panel"), this one carries Deckorate →
 * chat ("open this sheet"). Both exist because the requester and the renderer
 * live in DIFFERENT top-level tabs, so neither can reach the other by props
 * without threading a callback through every component in between.
 *
 * A request is served by two subscribers acting together, as in nav-bridge:
 *
 *   1. TaskPane switches the active tab to "chat".
 *   2. ChatTabPanel opens the requested sheet.
 *
 * Unlike nav-bridge there is no parked `pending` request, and deliberately so:
 * both subscribers are mounted for the whole session — TaskPane renders
 * ChatTabPanel in a CSS-hidden pane rather than unmounting it on a tab change —
 * so a listener is always registered when a request fires. Parking a target
 * that nothing needs to pick up later would only create a way for a stale
 * request to reopen a sheet the user never asked for.
 *
 * Today the only requester is the command palette ("All actions"), which can
 * reach a sheet-backed action from either its in-pane form or its Office
 * Dialog form — the dialog posts the command back to the pane, so both paths
 * end up here.
 */

import type { ChipSheetId } from "../chipTargets";

type ChatSheetListener = (sheet: ChipSheetId) => void;

const listeners = new Set<ChatSheetListener>();

/** Subscribe to sheet-open requests. Returns an unsubscribe for effect cleanup. */
export function subscribeChatSheet(listener: ChatSheetListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Ask the add-in to open an in-pane chat sheet. Safe to call from outside React. */
export function requestChatSheet(sheet: ChipSheetId): void {
  for (const listener of listeners) listener(sheet);
}
